/*
  Reminders page (sections/reminders.liquid).

  Registered on `alpine:init` like the theme's other Alpine components, so this
  file only has to run before Alpine starts rather than before the markup is
  parsed. Everything Liquid knows — the proxy path, the market, the occasion
  icon map — arrives as JSON on the root element's data-config.

  Deliberately not wrapped in requestAnimationFrame as some of the theme's
  components are: that delayed the prefetch below by a frame, measured at
  ~400ms on a cold load.

  Data comes from Engine over the Shopify App Proxy. Shopify signs each request
  and appends logged_in_customer_id, so nothing here sends a customer id.
*/
(() => {
  const ICONS = ['cake', 'gift', 'star', 'heart', 'egg', 'baby', 'tree', 'flower'];
  const FALLBACK = { icon: 'gift', tint: '#E6E2D8' };

  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const MONTH_LENGTHS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const blankForm = () => ({ recipient_name: '', occasion_id: '', day: '', month: '', year: '', note: '' });

  async function apiRequest(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // ngrok's free-plan interstitial triggers on the browser User-Agent,
        // which Shopify's App Proxy forwards through. Inert on the real domain,
        // so sent unconditionally rather than branching.
        'ngrok-skip-browser-warning': '1',
        ...(options.headers || {}),
      },
    });

    if (response.status === 401) throw new Error('Your session has expired. Please sign in again.');

    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
      // Laravel validation errors arrive as { errors: { field: [msg] } }.
      throw new Error(
        (body.errors && Object.values(body.errors)[0]?.[0]) || body.message || 'Something went wrong.'
      );
    }

    return body;
  }

  // Alpine starts inside its own requestAnimationFrame, which on a cold load
  // left the proxy calls sitting idle for ~430ms after this file had run. They
  // only need the config on the element, so they go now and init() awaits them.
  function fetchPage(config) {
    const query = new URLSearchParams({ country: config.country, market: config.market });

    return Promise.all([
      apiRequest(`${config.root}/me?${query}`),
      apiRequest(`${config.root}/occasions?${query}`),
      apiRequest(`${config.root}/list`),
    ]).then(
      ([me, occasions, list]) => ({ me, occasions, list }),
      // Settled rather than left rejected: nothing awaits this until Alpine
      // boots, and an unhandled rejection in between is a console error.
      (error) => ({ error })
    );
  }

  for (const el of document.querySelectorAll('.reminders-page[data-config]')) {
    const config = JSON.parse(el.dataset.config);

    el.remindersBoot = { config, page: config.signedIn ? fetchPage(config) : null };
  }

  document.addEventListener('alpine:init', () => {
    Alpine.data('xReminders', () => ({
      ready: false,
      saving: false,
      deleting: false,
      error: '',
      formError: '',
      removeError: '',

      me: null,
      occasions: [],
      reminders: [],

      // Usually the current market's catalogue, but on an edit it is the
      // catalogue of the market the reminder was created in — Engine validates
      // an edit against that market, not wherever the customer is browsing.
      formOccasions: [],
      // Whether the Day and Month fields hold a store date rather than the
      // customer's own, so switching occasion knows to clear them.
      dateWasShown: false,
      toast: '',
      toastTimer: null,
      maxNameLength: 60,
      // Engine's limits, replaced by what /occasions sends.
      maxNoteLength: 60,
      yearMin: 1900,
      yearMax: new Date().getFullYear(),

      formOpen: false,
      editing: null,
      removing: null,
      menu: null,
      returnFocusTo: null,

      form: blankForm(),

      // Set after a create so the form can stay open and offer the next one.
      saved: '',
      savedName: '',
      justCreated: null,
      upsell: [],

      months: MONTHS,

      init() {
        const boot = this.$el.remindersBoot;

        Object.assign(this, boot.config);

        if (boot.page) this.apply(boot.page);
      },

      // ------------------------------------------------------------ occasions

      styleFor(handle) {
        const mapped = this.occasionStyles[handle];

        if (!mapped) return FALLBACK;

        return {
          icon: ICONS.includes(mapped.icon) ? mapped.icon : FALLBACK.icon,
          tint: mapped.tint || FALLBACK.tint,
        };
      },

      // Curated in the section settings by handle. An empty list falls back to
      // the whole catalogue rather than hiding the strip, so a market that
      // gains an occasion does not silently lose its chips.
      get quickOccasions() {
        const wanted = this.quickAddHandles.split(',').map((h) => h.trim()).filter(Boolean);

        if (!wanted.length) return this.datedFirst(this.occasions);

        return wanted.map((h) => this.occasions.find((o) => o.handle === h)).filter(Boolean);
      },

      // Occasions the store already has a date for come first, because those
      // really are one tap.
      datedFirst(list) {
        return [...list].sort((a, b) => a.requires_date - b.requires_date);
      },

      // Just the label: a fixed date is shown in the locked Day and Month
      // fields and the line beneath them, as the design has it.
      occasionOptionLabel(occasion) {
        return occasion.label;
      },

      // The store's date for this occasion in this market, when it has one
      // the page may state. Never for a moveable occasion: its stored date is
      // only as current as the last time somebody set it.
      fixedDateFor(occasion) {
        if (!occasion || occasion.requires_date || occasion.moveable) return null;
        if (!occasion.event_day || !occasion.event_month) return null;

        return { day: Number(occasion.event_day), month: Number(occasion.event_month) };
      },

      // A date the customer does not choose: the store has one, or it moves.
      get dateLocked() {
        return !!this.selectedOccasion && !this.selectedOccasion.requires_date;
      },

      get dateHint() {
        const occasion = this.selectedOccasion;

        if (!this.dateLocked) return 'Go with your best guess';
        if (occasion.moveable) return `${occasion.label} changes each year — we’ll fill it in for you.`;

        const date = this.fixedDateFor(occasion);

        if (!date) return 'The store sets the date for this occasion.';

        // The next time it falls, so "25th December 2026" and not a date that
        // has already been and gone this year.
        const today = new Date();
        const thisYear = new Date(today.getFullYear(), date.month - 1, date.day);
        const year = thisYear < new Date(today.getFullYear(), today.getMonth(), today.getDate())
          ? today.getFullYear() + 1
          : today.getFullYear();

        return `${occasion.label} is ${this.ordinal(date.day)} ${MONTHS[date.month - 1]} ${year} — filled in for you.`;
      },

      // The Day and Month fields show the store's date on a locked occasion.
      // Display only: payload() sends a date for customer-dated occasions
      // alone, and Engine rejects one on an occasion that does not take it.
      showOccasionDate() {
        const date = this.fixedDateFor(this.selectedOccasion);

        this.form.day = date ? date.day : '';
        this.form.month = date ? date.month : '';
      },

      // ------------------------------------------------------------- the wall

      // Soonest first — a reminders page that does not lead with the next thing
      // coming is a list, not a reminder. Engine resolved every date already.
      get sortedReminders() {
        return [...this.reminders].sort(
          (a, b) => (a.days_until ?? Infinity) - (b.days_until ?? Infinity)
        );
      },

      get comingUpNote() {
        // The header is server-rendered, so this runs before the data arrives —
        // and "nothing set yet" is a lie while we are still asking.
        if (!this.ready) return '';

        if (!this.reminders.length) {
          return 'Nothing set yet — your first one takes about ten seconds.';
        }

        const next = this.sortedReminders[0];

        // No countdown for a moveable occasion: it is measured from a date only
        // as current as the last time somebody set it.
        return next.occasion.moveable
          ? `Next up: ${this.headlineFor(next)}.`
          : `Next up: ${this.headlineFor(next)}, ${this.countdown(next.days_until)}.`;
      },

      // Says why there is no date rather than pretending one is coming. The
      // stored date still drives the send; it is just not ours to state.
      moveableDateNote: 'Date changes each year',

      // A date the customer owns belongs to them — "Sophie's Birthday". A date
      // the calendar owns is something we mark for them — "Christmas for Sophie".
      // With a year, it says which one: "Izzy’s 4th Birthday". Engine works
      // out the number, so this never does date arithmetic of its own.
      headlineFor(reminder) {
        const which = reminder.years ? `${this.ordinal(reminder.years)} ` : '';

        return reminder.editable_date
          ? `${this.possessive(reminder.recipient_name)} ${which}${reminder.occasion.label}`
          : `${reminder.occasion.label} for ${reminder.recipient_name}`;
      },

      possessive(name) {
        const trimmed = (name || '').trim();

        if (!trimmed) return 'Their';

        return trimmed + (trimmed.endsWith('s') ? '’' : '’s');
      },

      countdown(days) {
        if (days === null || days === undefined) return '';
        if (days < 0) return 'has passed';
        if (days === 0) return 'today';
        if (days === 1) return 'tomorrow';

        return `in ${days} days`;
      },

      // Built from the parts rather than by toLocaleDateString, which follows
      // the BROWSER's locale — a UK customer on a US machine was shown
      // "December 25, 2026" on a British storefront. Engine sends a plain Y-m-d,
      // which Safari and Firefox also parse differently with no time zone.
      formatDate(value) {
        if (!value) return 'Date not set';

        const [year, month, day] = value.split('-').map(Number);

        return `${this.ordinal(day)} ${MONTHS[month - 1]} ${year}`;
      },

      // Days and ages alike: 11th–13th, 111th–113th, never "111st".
      ordinal(n) {
        if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;

        return n + ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
      },

      // ----------------------------------------------------------- the form

      get selectedOccasion() {
        return this.formOccasions.find((o) => String(o.id) === String(this.form.occasion_id));
      },

      // `ignore` is the one reminder that is not competition for this pair —
      // the row being edited, or the row this form has just created. Callers
      // that are asking about the list as it stands (the upsell tickboxes, the
      // post-save suggestions) pass nothing and count everything.
      //
      // `note` is only passed for an occasion that asks for one. There, two
      // reminders for one person are two different things when their notes
      // differ — "First day at nursery" and "First swimming lesson" — and
      // Engine allows both, so only the same note is a repeat.
      alreadySet(name, occasionId, ignore = null, note = undefined) {
        const who = (name || '').trim().toLowerCase();
        const what = (note || '').trim().toLowerCase();

        if (!who) return false;

        return this.reminders.some((r) =>
          String(r.occasion.id) === String(occasionId)
          && r.recipient_name.trim().toLowerCase() === who
          && (note === undefined || (r.note || '').trim().toLowerCase() === what)
          && r.id !== ignore
        );
      },

      // The quick-add chips and the post-save suggestions both make it easy to
      // land on an occasion this person already has, which Engine refuses with
      // a 422 after the fact. Only meaningful while the form is open: a closed
      // one still holds the last values it was given.
      //
      // The warning is about the reminder being composed, so it discounts the
      // one this form has just created. Without that, the list refresh after a
      // create hands the form back its own output and the form reads it as a
      // clash — the customer is told they already have the thing they asked for
      // a second earlier. The exemption lasts only as long as the form goes on
      // describing that reminder: choose another occasion, or open the form
      // again, and the list counts in full.
      get duplicate() {
        if (!this.formOpen || this.saved || !this.form.recipient_name || !this.form.occasion_id) return false;

        return this.alreadySet(
          this.form.recipient_name,
          this.form.occasion_id,
          this.editing?.id ?? this.justCreated,
          this.noteField ? this.form.note : undefined,
        );
      },

      get duplicateMessage() {
        const label = this.selectedOccasion?.label || 'reminder';
        const note = this.noteField ? this.form.note.trim() : '';

        return note
          ? `${this.form.recipient_name} already has a ${label} reminder for “${note}”.`
          : `${this.form.recipient_name} already has a ${label} reminder.`;
      },

      get canSave() {
        if (this.saving || this.saved || this.duplicate || this.futureDate || this.missingDay) return false;
        if (!this.form.recipient_name || !this.form.occasion_id) return false;
        if (this.noteField?.required && !this.form.note.trim()) return false;

        return this.selectedOccasion?.requires_date
          ? !!this.form.day && !!this.form.month
          : true;
      },

      // The extras the chosen occasion asks for. A year only where the
      // customer sets the date; a note wherever the occasion has one.
      get asksYear() {
        return !!this.selectedOccasion?.asks_year;
      },

      get noteField() {
        return this.selectedOccasion?.note ?? null;
      },

      // Newest first: the year somebody is looking for is usually recent.
      get years() {
        const years = [];

        for (let year = this.yearMax; year >= this.yearMin; year--) years.push(year);

        return years;
      },

      get chosenYear() {
        return this.asksYear && this.form.year ? Number(this.form.year) : null;
      },

      // A year makes the date a real one, which has to have happened — a
      // birth, not a due date. Engine refuses a future one too.
      isFuture(day, month, year) {
        if (!year || !month) return false;

        const today = new Date();
        const date = new Date(year, month - 1, day || 1);

        return date > new Date(today.getFullYear(), today.getMonth(), today.getDate());
      },

      get futureDate() {
        return this.isFuture(Number(this.form.day), Number(this.form.month), this.chosenYear);
      },

      // 29 February with a year that had none. The day is not quietly moved
      // to the 28th: with a year it is a date of birth, and changing it
      // without saying so would store one nobody gave. Day and year options
      // are greyed out so this is hard to reach, but an edit can arrive here.
      get missingDay() {
        return !!this.chosenYear && !!this.form.day && Number(this.form.day) > this.daysInMonth;
      },

      // A year is greyed out when the chosen day and month did not exist that
      // year, or have not happened yet in it.
      yearUnavailable(year) {
        const day = Number(this.form.day);
        const month = Number(this.form.month);

        if (!month) return false;

        return (day > 0 && day > new Date(year, month, 0).getDate())
          || this.isFuture(day, month, year);
      },

      // Options stay in the list (see clampDay) and are greyed out instead:
      // days the month does not have in the chosen year, and dates still to
      // come this year.
      dayUnavailable(day) {
        return day > this.daysInMonth
          || this.isFuture(day, Number(this.form.month), this.chosenYear);
      },

      monthUnavailable(month) {
        return this.isFuture(1, month, this.chosenYear);
      },

      // Offered alongside the one being created: occasions the store already
      // has a date for, so adding one really is a single tick.
      get upsellOccasions() {
        if (this.editing || this.saved || !this.form.recipient_name || !this.form.occasion_id) return [];

        return this.formOccasions.filter((o) =>
          !o.requires_date
          && !o.note?.required
          && String(o.id) !== String(this.form.occasion_id)
          && !this.alreadySet(this.form.recipient_name, o.id)
        );
      },

      // Offered under a heading that promises one click, so only occasions the
      // store already has a date for qualify: the name carries over from the
      // save, the date comes from the occasion, and the reminder really is one
      // press away. Anything needing a day and month would not be.
      get suggestions() {
        if (!this.saved || !this.savedName) return [];

        return this.formOccasions
          // A one-click reminder has nothing to say what it is for, so an
          // occasion that requires a note is not one.
          .filter((o) => !o.requires_date && !o.note?.required && !this.alreadySet(this.savedName, o.id))
          .slice(0, 3);
      },

      days: DAYS,

      // 29 February is a real birthday: without a year Engine stores the day
      // and month and clamps the 29th in non-leap years. With a year, the
      // month has the length it had that year.
      get daysInMonth() {
        const month = Number(this.form.month);

        if (month < 1 || month > 12) return 31;

        return this.chosenYear ? new Date(this.chosenYear, month, 0).getDate() : MONTH_LENGTHS[month - 1];
      },

      // Always 31 options rather than the selected month's length. A select
      // whose options shrink out from under x-model silently falls back to the
      // placeholder AND writes that empty value into the form, and the two
      // settle in the same flush so the order is not ours to choose. Fixed
      // options cannot do that; an impossible day is corrected here instead,
      // whichever of the two the customer changed.
      // Only against the month's own length, never the chosen year's: a 31st
      // in a 30-day month is a slip worth correcting, but 29 February in a
      // year without one is a real conflict the customer has to settle — see
      // missingDay.
      clampDay() {
        const month = Number(this.form.month);
        const longest = month >= 1 && month <= 12 ? MONTH_LENGTHS[month - 1] : 31;

        if (this.form.day && Number(this.form.day) > longest) {
          this.form.day = longest;
        }
      },

      // Onto a locked occasion, the fields show its date. Coming off one, the
      // day and month in the form were never the customer's, so they go —
      // back to the date they had set, on an edit, rather than a blank one to
      // pick again. The year is never overwritten by a locked date, so it
      // needs no restoring.
      onOccasionChange() {
        this.saved = '';
        this.upsell = [];
        // A new occasion is a new reminder, so the last one stops being exempt.
        this.justCreated = null;

        if (this.dateLocked) {
          this.showOccasionDate();
        } else if (this.dateWasShown) {
          this.form.day = this.editing?.day ?? '';
          this.form.month = this.editing?.month ?? '';
        }

        this.dateWasShown = this.dateLocked;
      },

      get removeMessage() {
        if (!this.removing) return '';

        return `We will stop emailing you about ${this.removing.occasion.label} for ${this.removing.recipient_name}.`;
      },

      // ---------------------------------------------------------- dialogs

      // A dialog with aria-modal="true" claims the whole page from a screen
      // reader, so focus has to actually be inside it and stay there.
      openDialog(panel) {
        this.returnFocusTo = document.activeElement;

        this.$nextTick(() => (this.focusableWithin(panel)[0] || panel).focus());
      },

      closeDialog() {
        const target = this.returnFocusTo;

        this.returnFocusTo = null;

        if (target?.focus) this.$nextTick(() => target.focus());
      },

      focusableWithin(panel) {
        if (!panel) return [];

        return [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      },

      // Tab out of the last control and you land in the page behind the overlay,
      // which is still scrollable and still clickable.
      trapFocus(event, panel) {
        const focusable = this.focusableWithin(panel);

        if (!focusable.length) return;

        const edge = event.shiftKey ? focusable[0] : focusable[focusable.length - 1];

        if (document.activeElement === edge) {
          event.preventDefault();
          (event.shiftKey ? focusable[focusable.length - 1] : focusable[0]).focus();
        }
      },

      // ------------------------------------------------------------- proxy

      // Engine resolves the market from the country code, because the theme's
      // market handles and Shopify's do not agree ('uk' vs 'gb'). The handle is
      // a secondary hint, and is sent only to the three endpoints that use it —
      // listing, editing and deleting are scoped to the customer, so a reminder
      // made in one store is found in all of them.
      get marketQuery() {
        return new URLSearchParams({ country: this.country, market: this.market }).toString();
      },

      request(path, options = {}) {
        return apiRequest(this.root + path, options);
      },

      // Nothing real renders until every call has answered, so the page never
      // shows an empty list it is about to fill in.
      async apply(page) {
        this.ready = false;
        this.error = '';

        const { me, occasions, list, error } = await page;

        if (error) {
          // `ready` stays false: the error panel replaces the page rather than
          // sitting above a list that never loaded.
          this.error = error.message;

          return;
        }

        this.me = me;
        this.occasions = occasions.occasions || [];

        // The form's select is inside an x-show dialog, so it is in the DOM from
        // the start — seeding its options here means they exist before anything
        // tries to select one. Without that, the first open renders options and
        // applies the value in the same flush, and the select silently keeps the
        // placeholder while the form believes an occasion is chosen.
        this.formOccasions = this.occasions;
        this.maxNameLength = occasions.limits?.recipient_name_max ?? this.maxNameLength;
        this.maxNoteLength = occasions.limits?.note_max ?? this.maxNoteLength;
        this.yearMin = occasions.limits?.year_min ?? this.yearMin;
        this.yearMax = occasions.limits?.year_max ?? this.yearMax;
        this.reminders = list.reminders || [];
        this.ready = true;
      },

      retry() {
        this.apply(fetchPage(this));
      },

      async reload() {
        this.reminders = (await this.request('/list')).reminders || [];
      },

      // -------------------------------------------------------- open / close

      resetForm() {
        this.editing = null;
        this.formError = '';
        this.saved = '';
        this.savedName = '';
        this.justCreated = null;
        this.upsell = [];
        this.form = blankForm();
        this.dateWasShown = false;
      },

      openCreate(occasionId = null, recipientName = '') {
        this.resetForm();
        this.menu = null;
        this.formOccasions = this.occasions;
        this.form.recipient_name = recipientName;
        this.form.occasion_id = occasionId ? String(occasionId) : '';
        this.dateWasShown = this.dateLocked;

        if (this.dateLocked) this.showOccasionDate();

        if (!this.formOpen) {
          this.formOpen = true;
          this.openDialog(this.$refs.formPanel);
        }
      },

      async openEdit(reminder) {
        this.resetForm();
        this.menu = null;
        this.editing = reminder;

        if (reminder.market && reminder.market !== this.me?.market) {
          let catalogue;

          try {
            catalogue = await this.occasionsFor(reminder.market);
          } catch (e) {
            // Engine validates an edit against the market the reminder was made
            // in, so opening with this market's catalogue instead would hide
            // the saved occasion and offer ones the update would refuse.
            this.editing = null;
            this.error = e.message;

            return;
          }

          // A second edit opened while that request was in flight owns the form
          // now; finishing this one would fill it with the wrong reminder's
          // values while `editing` points at the other, and the save would
          // write these details over that reminder.
          if (this.editing !== reminder) return;

          this.formOccasions = catalogue;
        } else {
          this.formOccasions = this.occasions;
        }

        // The options must exist before the value is applied. x-for and x-model
        // settle in the same flush, and a select holding a value with no
        // matching option silently falls back to the placeholder AND writes
        // that empty value back into the form.
        await this.$nextTick();

        if (this.editing !== reminder) return;

        this.form = {
          recipient_name: reminder.recipient_name,
          occasion_id: String(reminder.occasion.id),
          // Empty string rather than null so the selects match an <option>.
          day: reminder.day ?? '',
          month: reminder.month ?? '',
          year: reminder.year ?? '',
          note: reminder.note ?? '',
        };
        this.dateWasShown = this.dateLocked;

        if (this.dateLocked) this.showOccasionDate();

        this.formOpen = true;
        this.openDialog(this.$refs.formPanel);
      },

      // Throws rather than falling back: the caller cannot substitute this
      // market's catalogue for another market's without offering occasions the
      // save would reject.
      async occasionsFor(handle) {
        const response = await this.request(`/occasions?${new URLSearchParams({ market: handle })}`);

        return response.occasions || [];
      },

      // Resetting on close matters: `editing` clearing while the old values
      // stayed made a CLOSED form read as a duplicate, and the warning was
      // still on screen when the next create opened.
      closeForm() {
        if (this.saving) return;

        this.formOpen = false;
        this.resetForm();
        this.closeDialog();
      },

      // ------------------------------------------------------------- writes

      payload() {
        const payload = {
          occasion_id: this.form.occasion_id,
          recipient_name: this.form.recipient_name,
        };

        // Engine rejects a date on a fixed-date occasion rather than ignoring
        // it, so a stale day left from a previous selection must not travel.
        if (this.selectedOccasion?.requires_date) {
          payload.day = this.form.day || null;
          payload.month = this.form.month || null;
        }

        // Only where the occasion asks: Engine refuses either on an occasion
        // that does not, and a value kept from another occasion must not
        // travel. Null clears it on an edit.
        if (this.asksYear) payload.year = this.form.year || null;
        if (this.noteField) payload.note = this.form.note.trim() || null;

        return payload;
      },

      // Re-reading the list is not part of the write. A create that succeeded
      // and a /list that then failed must not be reported as a failed create —
      // the customer retries and Engine refuses it as a duplicate.
      async refresh() {
        try {
          await this.reload();
        } catch (e) {
          this.error = e.message;
        }
      },

      async save() {
        if (!this.canSave) return;

        this.saving = true;
        this.formError = '';

        // Taken before the first await: the form stays on screen while the
        // request is in flight, so reading either afterwards would save
        // whatever it had become rather than what was submitted.
        const editing = this.editing;
        const payload = this.payload();
        const name = this.form.recipient_name;
        const extras = [...this.upsell];

        try {
          if (editing) {
            // No market on an edit: Engine scopes it to the market the reminder
            // was created in, not wherever the customer is browsing from now.
            await this.request(`/${editing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
          } else {
            const created = await this.request(`?${this.marketQuery}`, { method: 'POST', body: JSON.stringify(payload) });

            this.justCreated = created.reminder?.id ?? null;
          }
        } catch (e) {
          this.formError = e.message;
          this.saving = false;

          return;
        }

        // Past this point the reminder exists. Nothing below may report failure
        // in a way that invites the customer to submit it again.
        const failed = [];

        // Ticked extras are separate reminders, so they are separate creates.
        for (const id of editing ? [] : extras) {
          try {
            await this.request(`?${this.marketQuery}`, {
              method: 'POST',
              body: JSON.stringify({ occasion_id: id, recipient_name: name }),
            });
          } catch (e) {
            failed.push(this.formOccasions.find((o) => String(o.id) === String(id))?.label || 'another reminder');
          }
        }

        if (editing) {
          await this.refresh();
          this.saving = false;
          this.closeForm();

          // Named as it now reads, not as it did before the edit.
          const updated = this.reminders.find((r) => r.id === editing.id) ?? editing;
          this.notify(`${this.headlineFor(updated)} updated.`);

          return;
        }

        // Settled before the list is re-read, not after. The refresh is what
        // puts the new reminder in front of the form's own duplicate check, so
        // by the time it lands the form has to already know it succeeded.
        this.savedName = name;
        this.saved = `${this.possessive(name)} Reminder Saved!`;
        this.upsell = [];
        this.formError = failed.length ? `We could not add ${failed.join(' or ')}.` : '';
        this.saving = false;

        await this.refresh();
      },

      confirmRemove(reminder) {
        this.removeError = '';
        this.removing = reminder;
        this.menu = null;
        this.openDialog(this.$refs.removePanel);
      },

      cancelRemove() {
        if (this.deleting) return;

        this.removing = null;
        this.closeDialog();
      },

      async remove() {
        if (!this.removing) return;

        this.deleting = true;
        this.removeError = '';

        try {
          await this.request(`/${this.removing.id}`, { method: 'DELETE' });
        } catch (e) {
          this.removeError = e.message;
          this.deleting = false;

          return;
        }

        // Deleted. A failed refresh is a stale list, not a failed delete.
        await this.refresh();

        const removed = this.removing;

        this.deleting = false;
        this.removing = null;
        this.closeDialog();
        this.notify(`${this.headlineFor(removed)} removed.`);
      },

      // The confirmation for actions that otherwise leave no trace once their
      // dialog closes. A create keeps its own message inside the form.
      notify(message) {
        this.toast = message;
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => { this.toast = ''; }, 4000);
      },
    }));
  });
})();
